import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
  Signal,
  WritableSignal,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationSkipped,
  Router,
  RouterLink,
  RouterLinkActive,
} from "@angular/router";
import { Store, select } from "@ngrx/store";
import { map, filter, mergeMap } from "rxjs/operators";
import { FaIconComponent } from "@fortawesome/angular-fontawesome";
import { Location } from "@angular/common";
import { CatalogueSelectors } from "../../store/selectors";
import { Category } from "../../../routes/catalog/models/category.model";
import { Product } from "../../../routes/catalog/models/product.model";
import { environment } from "../../../../environments/environment";

type MenuCategory = Category & {
  active?: boolean;
};

@Component({
  selector: "audio-menu",
  templateUrl: "./menu.component.html",
  styleUrls: ["./menu.component.sass"],
  imports: [RouterLink, RouterLinkActive, FaIconComponent],

  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenuComponent {
  private store = inject<Store>(Store);
  private router = inject(Router);
  activatedRoute = inject(ActivatedRoute);
  location = inject(Location);

  title = environment.title;

  now = new Date();

  routeParams$ = this.router.events.pipe(
    // tap((event) => console.log(event)),
    filter(
      (event) =>
        event instanceof NavigationEnd || event instanceof NavigationSkipped,
    ),
    map(() => this.activatedRoute),
    filter((activatedRoute) => {
      // console.log(activatedRoute);
      return !!activatedRoute;
    }),
    // tap((route) => console.log(route.snapshot.url)),
    map((route) => {
      while (route.firstChild) {
        route = route.firstChild;
      }
      return route;
    }),
    filter((route) => route.outlet === "primary"),
    mergeMap((route) => route.params),
  );

  $routeParams = toSignal(this.routeParams$);

  $categories: Signal<Category[]> = toSignal(
    this.store.pipe(select(CatalogueSelectors.getCategories)),
    {
      initialValue: [],
    },
  );

  public $catalogActive: WritableSignal<boolean> = signal(false);

  $activatedCategory: Signal<Category | null> = computed(
    () =>
      this.flattenCategories(this.$categories()).filter(
        (category) =>
          category.slug?.split("/")[0] === this.$routeParams()?.category,
      )[0],
  );

  $activatedProduct: Signal<Product | null> = computed(
    () =>
      this.flattenCategories(this.$categories())
        .flatMap((category) => category.products)
        .filter((product) => product.slug === this.$routeParams()?.product)[0],
  );

  $computedCategories = computed(() => {
    const categories = this.$categories?.();

    return categories?.map((category) => this.addActiveStatus(category)) ?? [];
  });

  constructor() {
    this.routeParams$.subscribe(() => {
      this.$catalogActive.set(this.router.url.indexOf("/location") > -1);
    });
  }

  addActiveStatus(category: MenuCategory): MenuCategory {
    // console.log("addActiveStatus", [category, this.isCategoryActive(category)]);
    return {
      ...category,
      active: category && this.isCategoryActive(category),
      children: category?.children?.map((child) => this.addActiveStatus(child)),
    };
  }

  isCategoryActive(category: MenuCategory): boolean {
    if (this.$activatedCategory()) {
      return (
        category?.slug === this.$activatedCategory()?.slug ||
        this.isChildrenActive(category)
      );
    }

    if (this.$activatedProduct()) {
      return (
        !!category.products?.filter(
          (product) => product.slug === this.$activatedProduct()?.slug,
        )?.length || this.isChildrenActive(category)
      );
    }

    return false;
  }

  isChildrenActive(entity: MenuCategory): boolean {
    return (
      !!entity.children?.length &&
      entity.children.reduce(
        (acc, child) => acc || this.isCategoryActive(child),
        false,
      )
    );
  }

  flattenCategories(categories: Category[]): Category[] {
    return categories.flatMap((category) => [
      category,
      ...this.flattenCategories(category.children),
    ]);
  }
}
